/*
 * One stable header on every page, including its persistent announcement.
 * No scroll threshold, row collapse, height animation or translated gap.
 * ResizeObserver measures content changes. Scroll reads cached geometry only.
 */
(() => {
  if (window.dacHeaderLayoutController) {
    window.dacHeaderLayoutController.init();
    return;
  }
  const root = document.documentElement;
  let cleanup;

  function init() {
    cleanup?.();
    const header = document.getElementById('shopify-section-header');
    const inner = header?.querySelector('.header-ios-fix');
    if (!inner || !header.querySelector('.header-dac, .cart-logo-bar')) return;

    const announcement = header.querySelector('.announcement-bar-top');
    const notices = header.querySelector('[data-dac-notices-bottom]');
    const marker = document.createElement('div');
    marker.setAttribute('data-dac-header-start', '');
    marker.setAttribute('aria-hidden', 'true');
    marker.style.cssText = 'height:0;width:0;margin:0;padding:0;border:0;overflow:hidden;';
    header.before(marker);

    let active = true, measureFrame = 0, scrollFrame = 0;
    let fullHeight = 0, documentTop = 0;
    const published = new Map();
    const px = value => (Math.round(value * 100) / 100) + 'px';

    function publish(target, name, value) {
      const key = (target === root ? 'root:' : 'header:') + name;
      if (published.get(key) === value) return;
      published.set(key, value);
      target.style.setProperty(name, value);
    }

    function position() {
      scrollFrame = 0;
      if (!active) return;
      const top = Math.max(documentTop - Math.max(0, window.scrollY), 0);
      publish(root, '--dac-header-bottom-y', px(Math.max(0, top + fullHeight)));
    }

    function measure() {
      measureFrame = 0;
      if (!active || !header.isConnected) return;
      // Read once, then publish. None of these properties changes row heights.
      fullHeight = inner.getBoundingClientRect().height;
      documentTop = marker.getBoundingClientRect().top + window.scrollY;
      const noticesHeight = notices?.getBoundingClientRect().height || 0;
      publish(root, '--dac-header-height', px(Math.max(0, fullHeight)));
      publish(root, '--dac-header-sticky-h', px(Math.max(0, fullHeight)));
      publish(root, '--dac-header-inner-h', px(fullHeight));
      publish(root, '--dac-header-bottom-height', px(noticesHeight));
      position();
    }

    function scheduleMeasure() {
      if (!measureFrame) measureFrame = requestAnimationFrame(measure);
    }
    function schedulePosition() {
      if (!scrollFrame) scrollFrame = requestAnimationFrame(position);
    }

    header.classList.remove('dac-header--compact');
    ['--dac-header-reserve', '--dac-header-shift', '--dac-header-offset', '--dac-search-row-h', '--dac-notices-row-h']
      .forEach(name => header.style.removeProperty(name));
    window.dispatchEvent(new CustomEvent('dac:header-state', { detail: false }));

    const observer = window.ResizeObserver ? new ResizeObserver(scheduleMeasure) : null;
    observer?.observe(inner);
    if (announcement) observer?.observe(announcement);
    window.addEventListener('scroll', schedulePosition, { passive: true });
    window.addEventListener('resize', scheduleMeasure, { passive: true });
    window.addEventListener('pageshow', scheduleMeasure);
    header.addEventListener('click', position, true);

    cleanup = () => {
      active = false;
      observer?.disconnect();
      cancelAnimationFrame(measureFrame);
      cancelAnimationFrame(scrollFrame);
      window.removeEventListener('scroll', schedulePosition);
      window.removeEventListener('resize', scheduleMeasure);
      window.removeEventListener('pageshow', scheduleMeasure);
      header.removeEventListener('click', position, true);
      marker.remove();
      header.style.removeProperty('--dac-announcement-scroll-h');
      ['--dac-header-height', '--dac-header-sticky-h', '--dac-header-inner-h', '--dac-header-bottom-height', '--dac-header-bottom-y']
        .forEach(name => root.style.removeProperty(name));
      cleanup = null;
    };
    measure();
  }

  window.dacHeaderLayoutController = { init };
  document.addEventListener('shopify:section:load', event => {
    if (event.target.id === 'shopify-section-header') init();
  });
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.id === 'shopify-section-header') cleanup?.();
  });
  init();
})();
